import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, Globe, ChevronRight, X, Sparkles, Loader2, LayoutGrid, Building2, Plus, Rocket, RefreshCcw } from "lucide-react";
import DashboardV2Layout from "../components/DashboardV2Layout";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import api, { API } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { toast } from "sonner";

/* Brand colours derived from the new logo */
const ACCENT_INK = "#0F172A";
const ACCENT_GREEN = "#10B981";
const PALETTE = [ACCENT_INK, ACCENT_GREEN, "#334155", "#64748B", "#94A3B8", "#CBD5E1"];

const RAG_STYLES = {
  healthy: { dot: "bg-emerald-500", chip: "bg-emerald-50 border-emerald-200 text-emerald-800", label: "On track" },
  needs_push: { dot: "bg-amber-500", chip: "bg-amber-50 border-amber-200 text-amber-900", label: "Needs push" },
  at_risk: { dot: "bg-rose-500", chip: "bg-rose-50 border-rose-200 text-rose-800", label: "At risk" },
  stale: { dot: "bg-slate-400", chip: "bg-slate-50 border-slate-200 text-slate-600", label: "Stale" },
};

const SCOPE_LABEL = {
  global: "Global workspace",
  regional: "Regional workspace",
  national: "National workspace",
  local: "Local workspace",
};

export default function LiveDashboard() {
  const [filters, setFilters] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState({ open: false, title: "", rows: [], total: 0, loading: false });
  const [downloading, setDownloading] = useState(false);

  const [selPrograms, setSelPrograms] = useState([]);
  const [selGeo, setSelGeo] = useState(null);
  const [selTheme, setSelTheme] = useState("");
  const [selAudience, setSelAudience] = useState("");
  const [showAllAxes, setShowAllAxes] = useState(false);
  // Scope toggle: 'portfolio' shows every accessible program, 'program'
  // focuses on a single one. We don't bake the program list into URL
  // params yet — keeping it client-side keeps the toggle snappy and the
  // Filters component handles the actual list state below.
  const [scopeMode, setScopeMode] = useState("portfolio");
  // Phase 2 — multi-dim filters
  const [selFunding, setSelFunding] = useState("");
  const [selPartner, setSelPartner] = useState("");
  const [selBenefGroup, setSelBenefGroup] = useState("");
  const [selPeriods, setSelPeriods] = useState([]); // multi-select
  // Phase 3 — calendar date range on uploaded_at
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/dashboard/filters")
      .then((r) => setFilters(r.data))
      .catch(() => toast.error("Could not load filters"));
  }, []);

  const refreshInsights = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        program_ids: selPrograms.length ? selPrograms : null,
        geo: selGeo,
        theme: selTheme || null,
        audience: selAudience || null,
        funding_source: selFunding || null,
        implementing_partner: selPartner || null,
        beneficiary_group: selBenefGroup || null,
        periods: selPeriods.length ? selPeriods : null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      };
      // Only need /insights here — DashboardV2Layout fetches its own v2
      // KPI grid + AI insights independently. The legacy `/scorecards`,
      // `/executive-brief` and `/data-quality` calls are no longer
      // surfaced in the UI after the Spring Cleaning pass.
      const insightsRes = await api.post("/dashboard/insights", body);
      setInsights(insightsRes.data);
    } catch (e) {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, [selPrograms, selGeo, selTheme, selAudience, selFunding, selPartner, selBenefGroup, selPeriods, dateFrom, dateTo]);

  // Phase 3.48 — real-time sync polling. Refresh dashboard every 10s
  // so submitted form rows surface without a manual refresh. Pauses when
  // the tab is hidden to avoid battery + Mongo load on idle tabs.
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { refreshInsights(); }, [refreshInsights]);

  useEffect(() => {
    if (insights) setLastUpdated(Date.now());
  }, [insights]);

  useEffect(() => {
    let timer = null;
    const tick = () => {
      if (document.hidden) return;
      refreshInsights();
    };
    timer = setInterval(tick, 10_000);
    return () => clearInterval(timer);
  }, [refreshInsights]);

  const manualRefresh = useCallback(async () => {
    setRefreshing(true);
    await refreshInsights();
    setRefreshing(false);
  }, [refreshInsights]);

  const openDrawer = async ({ title, indicator_id, axis, axis_value, period, geo, audience }) => {
    setDrawer({ open: true, title, rows: [], total: 0, loading: true });
    try {
      // When no programs are explicitly selected we want the audit to fall
      // back to "everything the user can see" rather than match against an
      // empty list (which yields 0 rows for system tiles like Total
      // Beneficiaries).
      const scoped = insights?.selected_programs || [];
      const r = await api.post("/dashboard/audit", {
        program_ids: scoped.length ? scoped : null,
        indicator_id: indicator_id || null,
        axis: axis || null,
        axis_value: axis_value || null,
        period: period || null,
        geo: geo || null,
        audience: audience || null,
        limit: 200,
      });
      setDrawer({ open: true, title, rows: r.data.rows, total: r.data.total, loading: false });
    } catch (e) {
      setDrawer((s) => ({ ...s, loading: false }));
      toast.error("Could not load audit rows");
    }
  };

  const downloadDataPack = async () => {
    setDownloading(true);
    try {
      const body = {
        program_ids: selPrograms.length ? selPrograms : null,
        geo: selGeo, theme: selTheme || null, audience: selAudience || null,
      };
      const r = await fetch(`${API}/dashboard/data-pack`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "array-engine-data-pack.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data pack downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const scope = insights?.scope || "local";
  const showRegion = ["regional", "national", "global"].includes(scope);
  const geoOptionsFiltered = useMemo(() => {
    if (!filters?.geography) return [];
    if (scope === "local") return filters.geography.filter((g) => g.axis === "City" || g.axis === "Location");
    if (scope === "national") return filters.geography.filter((g) => g.axis !== "Region");
    return filters.geography;
  }, [filters, scope]);

  // Click-to-Verify on KPI cards — drill into the audit drawer using
  // either an indicator id (Row 2 pinned cards) or a system tile key.
  const openKpiDrawer = useCallback((card) => {
    if (!card) return;
    if (card.indicator_id) {
      openDrawer({
        title: `${card.label || card.key}`,
        indicator_id: card.indicator_id,
      });
      return;
    }
    openDrawer({
      title: `${card.label || card.key}`,
      tile_key: card.key,
    });
  }, []);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Phase 3.45 — Onboarding empty state for users with zero programs.
            We deliberately wait for `filters` to resolve before deciding
            which branch to render so the regular dashboard never flashes
            for users who legitimately have no programs. */}
        {!filters ? (
          <DashboardBootSkeleton />
        ) : (filters.programs || []).length === 0 ? (
          <EmptyDashboardOnboarding />
        ) : (
        <>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="label-overline">Program Intelligence Center</p>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold mt-1" data-testid="pic-title">
              {scopeMode === "portfolio" ? "Portfolio command view" : "Program command view"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Click any KPI tile or chart to verify the source rows.
            </p>
            {/* Phase 3.48 — Live-sync indicator. Auto-refreshes every 10s
                so submitted form rows appear without a manual reload. */}
            <RealtimeStatus
              lastUpdated={lastUpdated}
              refreshing={refreshing}
              loading={loading}
              onRefresh={manualRefresh}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="minimal-filters-bar">
            <MinimalProgramFilter
              programs={filters?.programs || []}
              selected={selPrograms}
              onChange={setSelPrograms}
            />
            <MinimalLocationFilter
              geoOptions={geoOptionsFiltered}
              selGeo={selGeo}
              setSelGeo={setSelGeo}
              showRegion={showRegion}
            />
            <Button
              onClick={downloadDataPack}
              disabled={downloading || !insights?.selected_programs?.length}
              className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium disabled:opacity-50 h-9"
              data-testid="data-pack-button"
            >
              <Download className="w-4 h-4 mr-1.5" />
              {downloading ? "Packing…" : "Data pack"}
            </Button>
          </div>
        </div>

        {/* Advanced filters — collapsible for power users. */}
        <details className="group" data-testid="advanced-filters-details">
          <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 select-none">
            <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
            Advanced filters
          </summary>
          <div className="mt-3">
            <Filters
              filters={filters} showRegion={showRegion} geoOptions={geoOptionsFiltered}
              selPrograms={selPrograms} setSelPrograms={setSelPrograms}
              selGeo={selGeo} setSelGeo={setSelGeo}
              selTheme={selTheme} setSelTheme={setSelTheme}
              selAudience={selAudience} setSelAudience={setSelAudience}
              selFunding={selFunding} setSelFunding={setSelFunding}
              selPartner={selPartner} setSelPartner={setSelPartner}
              selBenefGroup={selBenefGroup} setSelBenefGroup={setSelBenefGroup}
              selPeriods={selPeriods} setSelPeriods={setSelPeriods}
              dateFrom={dateFrom} setDateFrom={setDateFrom}
              dateTo={dateTo} setDateTo={setDateTo}
              scopeMode={scopeMode}
            />
          </div>
        </details>

        <ProgramsBanner programs={filters?.programs || []} />

        <DashboardV2Layout selPrograms={selPrograms} onKpiClick={openKpiDrawer}>
          {loading && !insights && <SkeletonGrid />}
          {insights && (
            <ChartCard
              title="Trend"
              subtitle={`${insights.charts.line.series.length} period${insights.charts.line.series.length === 1 ? "" : "s"}`}
            >
              <TrendChart data={insights.charts.line.series} />
            </ChartCard>
          )}
        </DashboardV2Layout>
        </>
        )}
      </div>

      <AuditDrawer drawer={drawer} onClose={() => setDrawer((s) => ({ ...s, open: false }))} />
    </Layout>
  );
}

// Phase 3.48 — small status pill that shows when the dashboard data was
// last refreshed, with a manual "Refresh data" CTA fallback for users who
// don't want to wait the 10s polling interval.
function RealtimeStatus({ lastUpdated, refreshing, loading, onRefresh }) {
  const [tick, setTick] = useState(0);
  // Re-render every 5s so "X seconds ago" stays accurate even when polling
  // happens to land in the gap between updates.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5_000);
    return () => clearInterval(t);
  }, []);
  // Read tick so React knows this component depends on it (force re-render).
  void tick;
  let labelText = "Loading data…";
  if (lastUpdated) {
    const ageSec = Math.max(0, Math.floor((Date.now() - lastUpdated) / 1000));
    if (ageSec < 5) labelText = "Just now";
    else if (ageSec < 60) labelText = `${ageSec}s ago`;
    else if (ageSec < 3600) labelText = `${Math.floor(ageSec / 60)}m ago`;
    else labelText = `${Math.floor(ageSec / 3600)}h ago`;
  }
  return (
    <div
      className="mt-2 flex items-center gap-2 text-xs text-slate-500"
      data-testid="realtime-status"
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${loading || refreshing ? "bg-amber-400 animate-pulse" : "bg-emerald-500"}`}
        aria-hidden
      />
      <span data-testid="realtime-last-updated">Last updated: {labelText}</span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing || loading}
        className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 disabled:opacity-50 underline-offset-2 hover:underline"
        data-testid="realtime-refresh-btn"
      >
        <RefreshCcw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
        Refresh data
      </button>
    </div>
  );
}

// Phase 3.45 — Light skeleton shown while /dashboard/filters is in flight.
// Avoids flashing the full dashboard for users who turn out to have zero
// programs and would otherwise see Empty state milliseconds later.
function DashboardBootSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="dashboard-boot-skeleton">
      <div className="h-8 bg-slate-100 rounded-sm w-64" />
      <div className="h-4 bg-slate-100 rounded-sm w-96" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-slate-50 border border-slate-200 rounded-md" />
        ))}
      </div>
    </div>
  );
}

// Phase 3.45 — Empty-state onboarding shown on the dashboard when the
// user has zero accessible programs. Single-CTA prompt that drops them
// straight into the program creation wizard via `?new=1`.
function EmptyDashboardOnboarding() {
  return (
    <div
      className="max-w-2xl mx-auto py-16 text-center"
      data-testid="dashboard-empty-onboarding"
    >
      <div
        className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 mb-5"
        aria-hidden="true"
      >
        <Rocket className="w-6 h-6 text-emerald-600" />
      </div>
      <h2
        className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
        data-testid="dashboard-empty-heading"
      >
        Ready to measure your impact?
      </h2>
      <p
        className="mt-3 text-slate-600 text-base leading-relaxed max-w-lg mx-auto"
        data-testid="dashboard-empty-subhead"
      >
        Welcome to Array Engine. Start by creating your first program to see your impact data come to life here.
      </p>
      <div className="mt-8">
        <Link
          to="/programs?new=1"
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium rounded-sm px-5 py-2.5 transition-colors"
          data-testid="dashboard-empty-create-program-cta"
        >
          <Plus className="w-4 h-4" />
          Create Program
        </Link>
      </div>
    </div>
  );
}

/* ---------------- subcomponents ---------------- */

function ProgramsBanner({ programs }) {
  return (
    <Link
      to="/programs"
      className="flex items-center justify-between border border-slate-200 hover:border-slate-900 rounded-sm bg-white px-4 py-3 transition-colors"
      data-testid="programs-link"
    >
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 grid place-items-center rounded-sm bg-slate-900 text-white text-xs font-mono font-semibold">
          {programs.length}
        </span>
        <div>
          <p className="font-heading text-sm font-medium">Programs</p>
          <p className="text-xs text-slate-500">Manage indicators, uploads, forms, and reports</p>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </Link>
  );
}

// ---------------------------------------------------------------------------
// MinimalProgramFilter / MinimalLocationFilter — top-right minimalist
// controls (Option B spec). Single dropdowns with chip-style trigger;
// share state with the full Filters drawer so changes propagate both ways.
// ---------------------------------------------------------------------------
function MinimalProgramFilter({ programs, selected, onChange }) {
  const label = !selected?.length
    ? "All programs"
    : selected.length === 1
      ? (programs.find((p) => p.id === selected[0])?.name || "1 program")
      : `${selected.length} programs`;
  return (
    <select
      value={selected?.[0] || ""}
      onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
      className="h-9 text-xs border border-slate-300 hover:border-slate-900 rounded-sm bg-white px-2.5 font-medium text-slate-700 max-w-[180px] truncate"
      data-testid="minimal-program-filter"
      title={label}
    >
      <option value="">All programs</option>
      {programs.map((p) => (
        <option key={p.id} value={p.id}>{p.name}</option>
      ))}
    </select>
  );
}

function MinimalLocationFilter({ geoOptions, selGeo, setSelGeo, showRegion }) {
  const cleared = !selGeo || (!selGeo.country && !selGeo.region);
  return (
    <select
      value={cleared ? "" : `${selGeo.country || ""}__${selGeo.region || ""}`}
      onChange={(e) => {
        if (!e.target.value) { setSelGeo(null); return; }
        const [country, region] = e.target.value.split("__");
        setSelGeo(country || region ? { country: country || null, region: region || null } : null);
      }}
      className="h-9 text-xs border border-slate-300 hover:border-slate-900 rounded-sm bg-white px-2.5 font-medium text-slate-700 max-w-[180px] truncate"
      data-testid="minimal-location-filter"
    >
      <option value="">All locations</option>
      {(geoOptions?.countries || []).map((c) => (
        <option key={`c-${c}`} value={`${c}__`}>{c}</option>
      ))}
      {showRegion && (geoOptions?.regions || []).map((r) => (
        <option key={`r-${r}`} value={`__${r}`}>{r}</option>
      ))}
    </select>
  );
}


function Filters({
  filters, showRegion, geoOptions,
  selPrograms, setSelPrograms,
  selGeo, setSelGeo,
  selTheme, setSelTheme,
  selAudience, setSelAudience,
  selFunding, setSelFunding,
  selPartner, setSelPartner,
  selBenefGroup, setSelBenefGroup,
  selPeriods, setSelPeriods,
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  scopeMode,
}) {
  if (!filters) return null;
  const allSelected = !selPrograms.length;
  const dimUnassigned = filters.dim_unassigned || {};
  const showAdvanced = (filters.funding_sources?.length || 0) > 0
    || (filters.implementing_partners?.length || 0) > 0
    || (filters.beneficiary_groups?.length || 0) > 0
    || (filters.periods?.length || 0) > 1;
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4" data-testid="dashboard-filters">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <FilterSelect
          label="Programs" testid="filter-programs"
          value={allSelected ? "" : "selected"}
          onChange={(v) => v === "" && setSelPrograms([])}
          renderOptions={() => (
            <>
              <option value="">All ({filters.programs.length})</option>
              {selPrograms.length > 0 && (
                <option value="selected" disabled>{selPrograms.length} selected</option>
              )}
            </>
          )}
        />
        {showRegion ? (
          <FilterSelect
            label="Geography" testid="filter-geo"
            value={selGeo ? `${selGeo.axis}::${selGeo.value}` : ""}
            onChange={(v) => {
              if (!v) { setSelGeo(null); return; }
              const [axis, value] = v.split("::");
              setSelGeo({ axis, value });
            }}
            renderOptions={() => (
              <>
                <option value="">All geographies</option>
                {geoOptions.map((g) => (
                  <option key={`${g.axis}::${g.value}`} value={`${g.axis}::${g.value}`}>
                    {g.axis} · {g.value} ({g.count})
                  </option>
                ))}
              </>
            )}
          />
        ) : (
          <FilterSelect
            label="City / Community" testid="filter-geo-local"
            value={selGeo ? `${selGeo.axis}::${selGeo.value}` : ""}
            onChange={(v) => {
              if (!v) { setSelGeo(null); return; }
              const [axis, value] = v.split("::");
              setSelGeo({ axis, value });
            }}
            renderOptions={() => (
              <>
                <option value="">All</option>
                {geoOptions.map((g) => (
                  <option key={`${g.axis}::${g.value}`} value={`${g.axis}::${g.value}`}>
                    {g.value} ({g.count})
                  </option>
                ))}
              </>
            )}
          />
        )}
        <FilterSelect
          label="Theme" testid="filter-theme"
          value={selTheme} onChange={setSelTheme}
          renderOptions={() => (
            <>
              <option value="">All themes</option>
              {(filters.themes || []).map((t) => (
                <option key={t.value} value={t.value}>{t.value} ({t.count})</option>
              ))}
            </>
          )}
        />
        <FilterSelect
          label="Audience" testid="filter-audience"
          value={selAudience} onChange={setSelAudience}
          renderOptions={() => (
            <>
              <option value="">All audiences</option>
              {(filters.audiences || []).map((a) => (
                <option key={a.value} value={a.value}>{a.value} ({a.count})</option>
              ))}
            </>
          )}
        />
      </div>

      {showAdvanced && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="label-overline !text-slate-400 mb-2">BI dimensions</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <FilterSelect
              label="Funding source" testid="filter-funding"
              value={selFunding} onChange={setSelFunding}
              renderOptions={() => (
                <>
                  <option value="">All sources</option>
                  {(filters.funding_sources || []).map((f) => (
                    <option key={f.value} value={f.value}>{f.value} ({f.count})</option>
                  ))}
                  {dimUnassigned.funding_source > 0 && (
                    <option value="unassigned">Unassigned ({dimUnassigned.funding_source})</option>
                  )}
                </>
              )}
            />
            <FilterSelect
              label="Implementing partner" testid="filter-partner"
              value={selPartner} onChange={setSelPartner}
              renderOptions={() => (
                <>
                  <option value="">All partners</option>
                  {(filters.implementing_partners || []).map((p) => (
                    <option key={p.value} value={p.value}>{p.value} ({p.count})</option>
                  ))}
                  {dimUnassigned.implementing_partner > 0 && (
                    <option value="unassigned">Unassigned ({dimUnassigned.implementing_partner})</option>
                  )}
                </>
              )}
            />
            <FilterSelect
              label="Beneficiary group" testid="filter-beneficiary"
              value={selBenefGroup} onChange={setSelBenefGroup}
              renderOptions={() => (
                <>
                  <option value="">All groups</option>
                  {(filters.beneficiary_groups || []).map((b) => (
                    <option key={b.value} value={b.value}>{b.value} ({b.count})</option>
                  ))}
                  {dimUnassigned.beneficiary_group > 0 && (
                    <option value="unassigned">Unassigned ({dimUnassigned.beneficiary_group})</option>
                  )}
                </>
              )}
            />
            <FilterSelect
              label={`Period${selPeriods.length ? ` (${selPeriods.length})` : ""}`} testid="filter-period"
              value={selPeriods[0] || ""}
              onChange={(v) => setSelPeriods(v ? [v] : [])}
              renderOptions={() => (
                <>
                  <option value="">All periods</option>
                  {(filters.periods || []).slice(0, 50).map((p) => (
                    <option key={p.value} value={p.value}>{p.value} ({p.count})</option>
                  ))}
                </>
              )}
            />
          </div>
          {(filters.periods || []).length > 1 && (
            <PeriodMultiSelect
              periods={filters.periods || []}
              selected={selPeriods}
              onChange={setSelPeriods}
            />
          )}
          <div className="mt-3 pt-3 border-t border-slate-100/70" data-testid="advanced-date-range">
            <p className="label-overline !text-slate-400 mb-2">Advanced — calendar date range (uploaded_at)</p>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-slate-600 flex items-center gap-1.5">
                From
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="text-xs px-2 py-1 border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  data-testid="filter-date-from" />
              </label>
              <label className="text-xs text-slate-600 flex items-center gap-1.5">
                To
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="text-xs px-2 py-1 border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  data-testid="filter-date-to" />
              </label>
              {(dateFrom || dateTo) && (
                <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="text-[10px] text-slate-500 hover:text-slate-900 underline"
                  data-testid="filter-date-clear">Clear</button>
              )}
            </div>
          </div>
        </div>
      )}

      {(selGeo || selTheme || selAudience || selFunding || selPartner || selBenefGroup || selPeriods.length > 0) && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="label-overline !text-slate-400">Active filters</span>
          {selGeo && <FilterChip label={`${selGeo.axis}: ${selGeo.value}`} onClear={() => setSelGeo(null)} testid="chip-geo" />}
          {selTheme && <FilterChip label={`Theme: ${selTheme}`} onClear={() => setSelTheme("")} testid="chip-theme" />}
          {selAudience && <FilterChip label={`Audience: ${selAudience}`} onClear={() => setSelAudience("")} testid="chip-audience" />}
          {selFunding && <FilterChip label={`Funding: ${selFunding === "unassigned" ? "Unassigned" : selFunding}`} onClear={() => setSelFunding("")} testid="chip-funding" />}
          {selPartner && <FilterChip label={`Partner: ${selPartner === "unassigned" ? "Unassigned" : selPartner}`} onClear={() => setSelPartner("")} testid="chip-partner" />}
          {selBenefGroup && <FilterChip label={`Beneficiary: ${selBenefGroup === "unassigned" ? "Unassigned" : selBenefGroup}`} onClear={() => setSelBenefGroup("")} testid="chip-beneficiary" />}
          {selPeriods.length > 0 && <FilterChip label={`Periods: ${selPeriods.join(", ")}`} onClear={() => setSelPeriods([])} testid="chip-periods" />}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, renderOptions, testid }) {
  return (
    <label className="block text-xs">
      <span className="label-overline !text-slate-500 mb-1 block">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-sm bg-white text-sm px-2.5 py-2 focus:outline-none focus:border-slate-900"
        data-testid={testid}
      >
        {renderOptions()}
      </select>
    </label>
  );
}

function FilterChip({ label, onClear, testid }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm border bg-slate-50 border-slate-200 text-slate-700"
      data-testid={testid}
    >
      {label}
      <button onClick={onClear} className="text-slate-500 hover:text-slate-900" aria-label="Clear filter">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4" data-testid={`chart-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-baseline justify-between mb-3">
        <p className="font-heading text-sm font-medium">{title}</p>
        <p className="text-[11px] text-slate-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

const TOOLTIP_STYLE = { fontSize: 12, fontFamily: "inherit", borderRadius: 0, border: "1px solid #E2E8F0", background: "#fff" };
const AXIS_STYLE = { fontSize: 11, fill: "#64748B", fontFamily: "inherit" };

function TrendChart({ data }) {
  if (!data?.length) return <EmptyChart message="No periods reported yet." />;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis dataKey="period" tick={AXIS_STYLE} />
        <YAxis tick={AXIS_STYLE} width={56} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Line type="monotone" dataKey="value" stroke={ACCENT_GREEN} strokeWidth={2} dot={{ r: 3, fill: ACCENT_GREEN }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function AxisChartCard({ ax, onSlice, onHeatCell }) {
  const subtitle = `${ax.row_count} row${ax.row_count === 1 ? "" : "s"} · ${ax.distinct} categor${ax.distinct === 1 ? "y" : "ies"}`;
  // Allow toggling between donut / bar regardless of what the backend
  // recommended. Heatmap stays as a separate panel below since it's a
  // different visualization (period × axis) — switching takes 1 click.
  // The parent .map() keys this component on ax.axis, so a different
  // axis remounts the card and re-initialises chartType naturally.
  const [chartType, setChartType] = useState(ax.chart_type === "donut" ? "donut" : "bar");
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4" data-testid={`axis-${ax.axis}`}>
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-heading text-sm font-medium capitalize truncate">{ax.axis.replaceAll("_", " ")}</p>
            <span
              className="text-[9px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border bg-slate-50 border-slate-200 text-slate-600"
              title={`Auto-discovered ${ax.kind} axis`}
            >
              {ax.kind}
            </span>
          </div>
          <p className="text-[11px] text-slate-500">{subtitle}</p>
        </div>
        <ChartTypeSwitch
          axis={ax.axis}
          value={chartType}
          onChange={setChartType}
        />
      </div>
      {chartType === "donut"
        ? <AxisDonut slices={ax.slices} onSlice={onSlice} />
        : <AxisBar slices={ax.slices} onSlice={onSlice} />}
      {ax.heatmap && <Heatmap heatmap={ax.heatmap} axis={ax.axis} onCell={onHeatCell} />}
    </div>
  );
}

// Compact donut/bar toggle. The donut option is hidden when the axis has
// more than ~12 categories (pie charts collapse into a stripe of slivers).
function ChartTypeSwitch({ axis, value, onChange }) {
  return (
    <div
      className="inline-flex p-0.5 bg-slate-50 rounded-sm border border-slate-200 text-[10px] font-medium text-slate-500"
      role="tablist"
      aria-label={`Chart type for ${axis}`}
      data-testid={`chart-switch-${axis}`}
    >
      {[
        { key: "donut", label: "Donut" },
        { key: "bar", label: "Bar" },
      ].map((o) => (
        <button
          key={o.key}
          type="button"
          role="tab"
          aria-selected={value === o.key}
          onClick={() => onChange(o.key)}
          className={`px-1.5 py-0.5 rounded-sm transition-colors ${
            value === o.key ? "bg-white text-slate-900 shadow-sm border border-slate-200" : "hover:text-slate-900"
          }`}
          data-testid={`chart-switch-${axis}-${o.key}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AxisDonut({ slices, onSlice }) {
  if (!slices?.length) return <EmptyChart message="No data." />;
  const total = slices.reduce((s, x) => s + (x.value || 0), 0);
  return (
    <div className="grid grid-cols-2 gap-2 items-center">
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={slices} dataKey="value" nameKey="label"
            cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={1} stroke="#fff"
            onClick={(_, idx) => onSlice(slices[idx])} cursor="pointer">
            {slices.map((s, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip contentStyle={TOOLTIP_STYLE} />
        </PieChart>
      </ResponsiveContainer>
      <ul className="text-[11px] space-y-1.5">
        {slices.map((s, i) => {
          const pct = total > 0 ? Math.round(((s.value || 0) / total) * 100) : 0;
          return (
            <li key={s.label} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
              <button onClick={() => onSlice(s)} className="truncate hover:underline text-left" title={s.label}>{s.label}</button>
              <span className="ml-auto font-mono text-slate-600">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AxisBar({ slices, onSlice }) {
  if (!slices?.length) return <EmptyChart message="No data." />;
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, slices.length * 24)}>
      <BarChart data={slices} layout="vertical" margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
        <XAxis type="number" tick={AXIS_STYLE} />
        <YAxis type="category" dataKey="label" tick={AXIS_STYLE} width={90} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Bar dataKey="value" fill={ACCENT_INK} radius={0} cursor="pointer" onClick={(_, idx) => onSlice(slices[idx])} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* Slate ramp heatmap with green outline on the cell that hits the max value */
function Heatmap({ heatmap, axis, onCell }) {
  const { periods, labels, cells, max } = heatmap;
  if (!periods?.length || !labels?.length) return null;
  const valueByKey = Object.fromEntries(cells.map((c) => [`${c.period}__${c.label}`, c]));
  // Slate ramp: 0 → very light slate, max → near-black slate
  const tone = (v) => {
    if (!max || v === 0) return "#F8FAFC";
    const t = Math.min(1, v / max);
    // interpolate slate-100 (#F1F5F9) → slate-900 (#0F172A)
    const c1 = [241, 245, 249], c2 = [15, 23, 42];
    const c = c1.map((a, i) => Math.round(a + (c2[i] - a) * t));
    return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
  };
  return (
    <div className="mt-4 pt-3 border-t border-slate-100" data-testid={`heatmap-${axis}`}>
      <p className="label-overline mb-2">Period × {axis.replaceAll("_", " ")}</p>
      <div className="overflow-x-auto">
        <table className="border-collapse">
          <thead>
            <tr>
              <th className="p-1.5"></th>
              {periods.map((p) => (
                <th key={p} className="p-1.5 font-mono text-[10px] text-slate-500 font-normal">{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map((label) => (
              <tr key={label}>
                <th className="p-1.5 pr-3 text-[10px] text-slate-700 text-right font-medium capitalize whitespace-nowrap">{label}</th>
                {periods.map((p) => {
                  const cell = valueByKey[`${p}__${label}`] || { value: 0, period: p, label };
                  const isMax = max > 0 && cell.value === max;
                  const dark = max > 0 && cell.value / max > 0.55;
                  return (
                    <td key={p} className="p-0.5">
                      <button
                        onClick={() => onCell(cell)}
                        className="w-14 h-9 rounded-sm flex items-center justify-center font-mono text-[10px] transition-transform hover:scale-105 cursor-pointer"
                        style={{
                          background: tone(cell.value),
                          color: dark ? "#fff" : "#1E293B",
                          outline: isMax ? "2px solid #10B981" : "none",
                          outlineOffset: "-2px",
                        }}
                        data-testid={`heat-${axis}-${p}-${label}`}
                        title={`${label} · ${p}: ${cell.value.toLocaleString()}`}
                      >
                        {cell.value > 0 ? cell.value.toLocaleString() : "—"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyChart({ message }) {
  return (
    <div className="h-[220px] flex items-center justify-center text-xs text-slate-400 italic px-4 text-center">
      {message}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 bg-slate-50 border border-slate-200 rounded-sm animate-pulse" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => <div key={i} className="h-64 bg-slate-50 border border-slate-200 rounded-sm animate-pulse" />)}
      </div>
    </div>
  );
}

function AuditDrawer({ drawer, onClose }) {
  return (
    <Sheet open={drawer.open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="audit-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading">{drawer.title || "Audit trail"}</SheetTitle>
          <SheetDescription>
            {drawer.loading ? "Loading source rows…" : `${drawer.total} source row${drawer.total === 1 ? "" : "s"} · showing ${drawer.rows.length}`}
          </SheetDescription>
        </SheetHeader>
        {drawer.loading ? (
          <div className="mt-6 space-y-2">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-12 bg-slate-50 border border-slate-100 rounded-sm animate-pulse" />)}
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            {drawer.rows.length === 0 && <p className="text-sm text-slate-500 italic">No source rows match this slice yet.</p>}
            {drawer.rows.map((r) => (
              <div key={r.id} className="border border-slate-200 rounded-sm p-3 text-xs" data-testid={`audit-row-${r.id}`}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-slate-500">{r.indicator_code}</span>
                  <span className="text-slate-700 truncate">{r.indicator_name}</span>
                  <span className="ml-auto font-mono">
                    {r.value_num !== null && r.value_num !== undefined
                      ? `${Number(r.value_num).toLocaleString()} ${r.unit || ""}`
                      : (r.value_text || "—")}
                  </span>
                </div>
                <div className="mt-1 text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                  <span>Period <span className="font-mono text-slate-700">{r.period}</span></span>
                  <span>File <span className="font-mono text-slate-700">{r.filename}</span> · row {r.row_index}</span>
                  {r.uploaded_by && <span>by {r.uploaded_by}</span>}
                </div>
                {r.disaggregation && Object.keys(r.disaggregation).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(r.disaggregation).map(([k, v]) => (
                      <span key={k} className="inline-block text-[10px] px-1.5 py-0.5 bg-slate-50 border border-slate-200 rounded-sm font-mono">
                        {k}: {String(v)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}


/* ============================================================
 * Phase 2.0 — Program Intelligence Center sub-components
 * ============================================================ */

function ScopeToggle({ mode, onChange }) {
  const opts = [
    { key: "portfolio", label: "Portfolio", icon: LayoutGrid },
    { key: "program", label: "Single program", icon: Building2 },
  ];
  return (
    <div
      className="inline-flex p-1 bg-slate-100 rounded-sm border border-slate-200"
      role="tablist"
      aria-label="Dashboard scope"
      data-testid="scope-toggle"
    >
      {opts.map((o) => {
        const Icon = o.icon;
        const active = mode === o.key;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.key)}
            className={`px-2.5 py-1 text-xs font-medium rounded-sm transition-colors flex items-center gap-1.5 ${
              active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
            data-testid={`scope-toggle-${o.key}`}
          >
            <Icon className="w-3 h-3" />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Phase 2c — multi-period checkbox grid + calendar date range. Renders
// only when the workspace has more than one period (otherwise the
// single-select dropdown above is enough).
function PeriodMultiSelect({ periods, selected, onChange }) {
  const [open, setOpen] = useState(false);
  // Group periods by year for a cleaner picker UX (Q1-2025, Q2-2025 etc.).
  const groups = useMemo(() => {
    const m = new Map();
    for (const p of periods) {
      const yr = (p.value.match(/\d{4}/) || ["other"])[0];
      if (!m.has(yr)) m.set(yr, []);
      m.get(yr).push(p);
    }
    return [...m.entries()].sort((a, b) => (b[0] || "").localeCompare(a[0] || ""));
  }, [periods]);
  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  return (
    <div className="mt-2 border border-slate-200 rounded-sm bg-slate-50/40" data-testid="period-multi-select">
      <button
        type="button"
        className="w-full px-2.5 py-1.5 flex items-center justify-between text-xs text-slate-600 hover:text-slate-900"
        onClick={() => setOpen((o) => !o)}
        data-testid="period-multi-toggle"
      >
        <span>Multi-period selection {selected.length ? `· ${selected.length} chosen` : "· optional"}</span>
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 border-t border-slate-200" data-testid="period-multi-grid">
          {groups.map(([yr, list]) => (
            <div key={yr} className="mt-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{yr}</p>
              <div className="flex flex-wrap gap-1.5">
                {list.map((p) => {
                  const on = selected.includes(p.value);
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => toggle(p.value)}
                      className={`text-[11px] px-1.5 py-0.5 rounded-sm border transition-colors ${
                        on
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-900"
                      }`}
                      data-testid={`period-multi-opt-${p.value}`}
                    >
                      {p.value} <span className="opacity-60">({p.count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[10px] text-slate-500 hover:text-slate-900 mt-2"
              data-testid="period-multi-clear"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

